import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, Package, LayoutList, Archive,
  Store, Users, CreditCard, Wallet, Star, Tag,
  FileText, BarChart3, MessageSquare, Bell, Settings, User, 
  LogOut, Leaf, ChevronLeft, ChevronRight, Menu, X,
  TrendingUp, Shield, HelpCircle, Award, Gift
} from 'lucide-react';
import { useUser } from '../../context/UserContext';

const menuGroups = [
  {
    heading: 'Main',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/admin', color: '#10b981' }
    ]
  },
  {
    heading: 'Commerce',
    items: [
      { icon: ShoppingCart, label: 'Orders', href: '/admin/orders', color: '#3b82f6', count: 12 },
      { icon: Package, label: 'Products', href: '/admin/products', color: '#f59e0b' },
      { icon: LayoutList, label: 'Categories', href: '/admin/categories', color: '#8b5cf6' },
      { icon: Archive, label: 'Inventory', href: '/admin/inventory', color: '#06b6d4' },
      { icon: Archive, label: 'Returns', href: '/admin/returns', color: '#ef4444' }
    ]
  },
  {
    heading: 'Users',
    items: [
      { icon: Store, label: 'Sellers', href: '/admin/sellers', color: '#10b981' },
      { icon: Users, label: 'Customers', href: '/admin/customers', color: '#3b82f6' }
    ]
  },
  {
    heading: 'Finance',
    items: [
      { icon: CreditCard, label: 'Payments', href: '/admin/payments', color: '#f59e0b' },
      { icon: Wallet, label: 'Payouts', href: '/admin/payouts', color: '#8b5cf6' }
    ]
  },
  {
    heading: 'Engagement',
    items: [
      { icon: Star, label: 'Reviews', href: '/admin/reviews', color: '#f59e0b' },
      { icon: Tag, label: 'Coupons', href: '/admin/coupons', color: '#10b981' },
      { icon: Gift, label: 'Offers', href: '/admin/offers', color: '#ef4444' }
    ]
  },
  {
    heading: 'Insights',
    items: [
      { icon: FileText, label: 'Reports', href: '/admin/reports', color: '#8b5cf6' },
      { icon: BarChart3, label: 'Analytics', href: '/admin/analytics', color: '#06b6d4' }
    ]
  },
  {
    heading: 'Communication',
    items: [
      { icon: MessageSquare, label: 'Messages', href: '/admin/messages', color: '#3b82f6' },
      { icon: Bell, label: 'Notifications', href: '/admin/notifications', color: '#f59e0b' }
    ]
  },
  {
    heading: 'System',
    items: [
      { icon: User, label: 'Profile', href: '/admin/profile', color: '#10b981' },
      { icon: LogOut, label: 'Logout', action: 'logout', color: '#ef4444' }
    ]
  }
];

const AdminSidebar = ({ collapsed, mobileOpen, onToggle, onMobileClose }) => {
  const location = useLocation();
  const { logout } = useUser();

  const isActive = (href) => {
    if (!href) return false;
    if (href === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  // Close sidebar on mobile when clicking a link
  const handleLinkClick = () => {
    if (window.innerWidth < 992 && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <Link to="/admin" className="sidebar-logo" onClick={handleLinkClick}>
          <div className="sidebar-logo-icon">
            <Leaf size={22} />
          </div>
          <span className="sidebar-logo-text">PaudhaAdmin</span>
          {!collapsed && <div className="sidebar-logo-badge">v2.0</div>}
        </Link>

        {/* Desktop Toggle Button */}
        <button 
          onClick={onToggle} 
          className="sidebar-toggle-btn desktop-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>

        {/* Mobile Close Button */}
        <button 
          onClick={onMobileClose} 
          className="sidebar-close-btn mobile-close"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>

        {/* Navigation */}
        <div className="sidebar-nav custom-scrollbar">
          {menuGroups.map((group, index) => (
            <div key={index} className="sidebar-group">
              {!collapsed && (
                <div className="sidebar-group-heading">
                  <span>{group.heading}</span>
                  <div className="sidebar-group-line"></div>
                </div>
              )}
              
              {collapsed && index > 0 && <div className="sidebar-divider"></div>}
              
              <div className="sidebar-group-items">
                {group.items.map((item, idx) => {
                  if (item.action === 'logout') {
                    return (
                      <button 
                        key="logout-btn"
                        onClick={handleLogout}
                        className="sidebar-link logout-btn"
                        title={collapsed ? item.label : ''}
                      >
                        <item.icon size={20} />
                        <span>{item.label}</span>
                      </button>
                    );
                  }

                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href || idx}
                      to={item.href || '#'}
                      className={`sidebar-link ${active ? 'active' : ''}`}
                      title={collapsed ? item.label : ''}
                      onClick={handleLinkClick}
                    >
                      <div className="sidebar-link-icon" style={{ color: active ? item.color : undefined }}>
                        <item.icon size={20} />
                      </div>
                      <span className="sidebar-link-text">{item.label}</span>
                      {item.count && (
                        <span className="sidebar-link-badge">{item.count}</span>
                      )}
                      {!collapsed && active && <div className="sidebar-link-active-indicator"></div>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-content">
            <div className="sidebar-footer-icon">
              <Shield size={16} />
            </div>
            {!collapsed && (
              <div className="sidebar-footer-text">
                <span>Secure Admin Panel</span>
                <small>Protected by Paudhawala</small>
              </div>
            )}
          </div>
        </div>
      </aside>

      <style jsx>{`
        .admin-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          width: 280px;
          background: linear-gradient(180deg, #1a472a 0%, #0d2b1a 100%);
          color: #e8ecef;
          transition: width 0.3s ease;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
        }

        .admin-sidebar.collapsed {
          width: 80px;
        }

        /* Logo */
        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          text-decoration: none;
          position: relative;
        }

        .sidebar-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .sidebar-logo-icon svg {
          color: white;
        }

        .sidebar-logo-text {
          font-size: 18px;
          font-weight: 700;
          color: white;
          letter-spacing: 0.5px;
        }

        .sidebar-logo-badge {
          position: absolute;
          right: 16px;
          top: 28px;
          font-size: 10px;
          padding: 2px 6px;
          background: rgba(16, 185, 129, 0.2);
          border-radius: 20px;
          color: #10b981;
          font-weight: 500;
        }

        .collapsed .sidebar-logo-badge {
          display: none;
        }

        .collapsed .sidebar-logo-text {
          display: none;
        }

        /* Toggle Button */
        .sidebar-toggle-btn {
          position: absolute;
          right: -16px;
          top: 24px;
          width: 32px;
          height: 32px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          z-index: 101;
        }

        .sidebar-toggle-btn:hover {
          transform: scale(1.1);
          background: #10b981;
          border-color: #10b981;
        }

        .sidebar-toggle-btn:hover svg {
          color: white;
        }

        .collapsed .sidebar-toggle-btn {
          right: -16px;
        }

        /* Mobile Close Button */
        .sidebar-close-btn {
          display: none;
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          color: white;
          cursor: pointer;
          z-index: 102;
        }

        /* Navigation */
        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 20px 12px;
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 4px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
        }

        /* Group */
        .sidebar-group {
          margin-bottom: 20px;
        }

        .sidebar-group-heading {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding-left: 12px;
        }

        .sidebar-group-heading span {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(255, 255, 255, 0.5);
        }

        .sidebar-group-line {
          flex: 1;
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
        }

        .sidebar-divider {
          height: 1px;
          background: rgba(255, 255, 255, 0.08);
          margin: 12px 8px;
        }

        /* Links */
        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          margin-bottom: 4px;
          border-radius: 12px;
          color: #cbd5e1;
          text-decoration: none;
          transition: all 0.3s ease;
          position: relative;
        }

        .sidebar-link:hover {
          background: rgba(255, 255, 255, 0.08);
          color: white;
          transform: translateX(4px);
        }

        .sidebar-link.active {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .sidebar-link.active .sidebar-link-icon svg {
          color: #10b981;
        }

        .sidebar-link-icon {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sidebar-link-text {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }

        .sidebar-link-badge {
          padding: 2px 8px;
          background: #ef4444;
          color: white;
          font-size: 10px;
          font-weight: 600;
          border-radius: 20px;
        }

        .sidebar-link-active-indicator {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 24px;
          background: #10b981;
          border-radius: 3px;
        }

        /* Logout Button */
        .logout-btn {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          margin-top: 8px;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .logout-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .logout-btn .sidebar-link-icon svg {
          color: #ef4444;
        }

        /* Footer */
        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          margin-top: auto;
        }

        .sidebar-footer-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .sidebar-footer-icon {
          width: 28px;
          height: 28px;
          background: rgba(16, 185, 129, 0.15);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sidebar-footer-text {
          display: flex;
          flex-direction: column;
        }

        .sidebar-footer-text span {
          font-size: 12px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
        }

        .sidebar-footer-text small {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.5);
        }

        .collapsed .sidebar-footer-content {
          justify-content: center;
        }

        .collapsed .sidebar-footer-text {
          display: none;
        }

        /* Mobile Responsive */
        @media (max-width: 991px) {
          .admin-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            width: 280px;
          }

          .admin-sidebar.mobile-open {
            transform: translateX(0);
          }

          .sidebar-close-btn {
            display: flex;
          }

          .desktop-toggle {
            display: none;
          }
        }

        @media (min-width: 992px) {
          .admin-sidebar.collapsed ~ .admin-main-content {
            margin-left: 80px;
          }
          
          .admin-main-content {
            margin-left: 280px;
            transition: margin-left 0.3s ease;
          }
        }
      `}</style>
    </>
  );
};

export default AdminSidebar;